// Shared request/response pieces: path parameters, cursor pagination (04 §Pagination) and CSV
// imports (13 §Imports).
import { z } from "zod";
import { ImportResource, ImportRowStatus } from "../enums";
import { Timestamp, Uuid } from "../primitives";

export const CompanyParams = z.object({ co: Uuid.describe("Company id") });
export const CompanyItemParams = z.object({ co: Uuid, id: Uuid });

/** `?limit=` (default 50, max 200) and `?cursor=` (opaque, signed, company-bound). */
export const PageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).optional(),
  cursor: z.string().max(1024).optional(),
});

/** `{ data, next_cursor, prev_cursor }`. */
export function page<T extends z.ZodType>(item: T) {
  return z.object({
    data: z.array(item),
    next_cursor: z.string().nullable(),
    prev_cursor: z.string().nullable(),
  });
}

/** Multipart CSV upload; `dry_run=true` validates every row and writes nothing. */
export const ImportUpload = z
  .object({
    file: z.string().describe("CSV file (multipart part `file`)").meta({ format: "binary" }),
    dry_run: z.enum(["true", "false"]).optional(),
    external_identities: z
      .string()
      .optional()
      .describe(
        "time-entries only: an external-identities CSV committed in the same batch, so its mappings resolve the time rows (S1-11 acceptance 8)",
      )
      .meta({ format: "binary" }),
  })
  .meta({ id: "ImportUpload" });

export const ImportRowResult = z
  .object({
    row: z.int().min(1).describe("1-based data row number (header excluded)"),
    status: ImportRowStatus,
    ref: Uuid.optional().describe("Row written or matched"),
    errors: z
      .array(z.object({ column: z.string().optional(), code: z.string(), message: z.string() }))
      .optional(),
  })
  .meta({ id: "ImportRowResult" });

export const ImportResult = z
  .object({
    batch_id: Uuid.describe("correlation_id of every event the batch writes"),
    resource: ImportResource,
    dry_run: z.boolean(),
    rows: z.array(ImportRowResult),
    counts: z.partialRecord(ImportRowStatus, z.int().min(0)),
  })
  .meta({ id: "ImportResult" });

export const ImportBatch = z
  .object({
    batch_id: Uuid,
    resource: ImportResource,
    rows: z.int().min(0),
    committed_at: Timestamp,
    reversed_at: Timestamp.nullable(),
  })
  .meta({ id: "ImportBatch" });
